import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { faUserGroup, faChevronsRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Style from "@/lib/style";

type Props = {
  programsRef: any;
}

export default function ProgramsSelection (props: Props) {
  const [displaySection, setDisplaySection] = useState<number>(0);
  const program = Style[displaySection];

  return (
    <section ref={props.programsRef}>
      <div className={`relative chefs-cocina ${program.title}`}>
        {displaySection === 0 ? (
          <Image 
            src="/images/programas/basico.webp" 
            alt="Programa Básico" 
            fill 
            className="object-cover -z-10"
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
          />
        ) : displaySection === 1 ? (
          <Image 
            src="/images/programas/medio.jpg" 
            alt="Programa Medio" 
            fill 
            className="object-cover -z-10"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
          />
        ) : (
          <Image 
            src="/images/programas/profesional.jpg" 
            alt="Programa Profesional" 
            fill 
            className="object-cover -z-10"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
          />
        )}
        {program && program.sectionColors && (
          <div className={`${program.sectionColors[0].sectionColor} program-section`}>
            {program.sectionImages && (
              <div>
                <Image src={program.sectionImages[0].halfCircles} width={50} height={10} className={`${(program?.images[0] as {linePosition?: string }).linePosition || ''} hidden md:block absolute bottom-32 -right-[25px]`} alt="Decoration" unoptimized/>
                <Image src={program.sectionImages[0].line} width={400} height={100} className={`${(program?.images[0] as {linePosition?: string }).linePosition || ''} hidden md:block absolute top-0 left-0 z-10 rounded-br-[500px]`} alt="Decoration" unoptimized/>
                <Image src={program.sectionImages[0].circle} width={714} height={758} sizes="(max-width: 768px) 0px, 357px" className="absolute top-0 left-0 opacity-80 z-0" alt="Decoration" priority/>
                <Image src={program.sectionImages[0].halfDonut} width={110} height={56} sizes="56px" className="hidden md:block absolute bottom-0 -right-8 opacity-60" alt="Decoration"/>
              </div>
            )}
          </div>
        )}
        <div className="bigscreen-responsive flex transition ease-in-out delay-115 md:items-center">
          <div className="relative z-50 pt-16">
            <div>
              <div className="font-bold text-white text-2xl text-center py-8 px-3 transition ease-in-out delay-200 md:text-4xl md:pb-8 md:px-0 md:text-left">
                <h1>3 técnicos que se adaptan a <span className={`inline-block text-white relative after:absolute after:h-1 after:rounded-full after:block after:${program?.sectionColors?.[0]?.underlineColor ?? 'default-underline-color'} after:w-full after:-bottom-1 after:right-0`}>vos</span></h1>
              </div>
              <div className={`${program?.sectionColors?.[0]?.barColor ?? 'default-bar-color'} flex justify-between rounded-full bg-[#D4572840] opacity-95 px-1.5 py-1.5 transition ease-in-out delay-115 md:flex-row md:px-1.5 md:py-1.5 md:rounded-full w-[90%] mx-auto md:w-full`}>
                <button onClick={() => setDisplaySection(0)} className={`flex items-center px-4 py-2 md:px-8 md:py-2 rounded-full text-md md:text-lg ${displaySection !== 0 ? 'text-white' : 'font-bold text-green bg-white'}`}>
                  <FontAwesomeIcon icon={faUserGroup} className="hidden md:block mr-2" /><span>Básico</span>
                </button>
                <button onClick={() => setDisplaySection(1)} className={`flex items-center px-4 py-2 md:px-8 md:py-2 rounded-full text-md md:text-lg ${displaySection !== 1 ? 'text-white' : 'font-bold text-orange bg-white'}`}>
                  <FontAwesomeIcon icon={faUserGroup} className="hidden md:block mr-2" /><span>Medio</span>
                </button>
                <button onClick={() => setDisplaySection(2)} className={`flex items-center px-4 py-2 md:px-8 md:py-2 rounded-full text-md md:text-lg ${displaySection !== 2 ? 'text-white' : 'font-bold text-red bg-white'}`}>
                  <FontAwesomeIcon icon={faUserGroup} className="hidden md:block mr-2" /><span>Profesional</span>
                </button>
              </div>
            </div>
            {displaySection === 0 && (
              <div className="z-10 px-4">
                <div className="mt-8">
                  <h2 className="font-thin text-3xl md:text-5xl">Técnico <span className="font-bold">Básico</span></h2>
                </div>
                <div className="py-2 md:py-6">
                  <p className="uppercase text-lg text-white px-2 md:text-2xl">En <span className="font-bold">gastronomía</span></p>
                  <p className="font-thin mt-2 max-w-md text-md px-2 py-4 md:text-lg md:max-w-lg md:py-2"><span className="font-bold">Nutrí tu talento, alimenta tu curiosidad y construí los fundamentos</span> que necesitás en el mundo culinario más versátil de Costa Rica. Experimentá de primera mano con nuestros chefs instructores un programa con cursos 100% prácticos enfocadas en las bases técnicas culinarias.</p>
                </div>
                <div className="py-4 w-44 md:w-52 md:py-1">
                  <Link href='/programas/basico' className="flex items-center justify-center font-bold text-sm rounded-full py-3 px-4 shadow-sm border-2 border-green-light md:py-3 md:px-8 hover:bg-green-dark/35 md:text-nowrap">
                    <p className="mr-2 text-white text-md md:text-lg">Descubrir más</p>
                    <FontAwesomeIcon icon={faChevronsRight} className="text-[9px] text-white" />
                  </Link>
                </div>
              </div>
            )}
            {displaySection === 1 && (
              <div className="z-10 px-4">
                <div className="mt-8">
                  <h2 className="font-thin text-3xl md:text-5xl">Técnico <span className="font-bold">Medio</span></h2>
                </div>
                <div className="py-2 md:py-6">
                  <p className="uppercase text-white text-lg px-2 md:text-2xl">En <span className="font-bold">gastronomía</span></p>
                  <p className="font-thin mt-2 max-w-md text-md px-2 py-1 md:text-lg md:max-w-lg md:py-2"><span className="font-bold">Hacé realidad ese proyecto gastronómico que tanto anhelas mientras aprendés cocinando desde el primer día.</span> Elevá tu conocimiento gastronómico mediante nuestro programa que ofrece las bases técnicas culinarias donde podrás aplicar en recetas de cocina internacional.</p>
                </div>
                <div className="py-4 w-44 md:w-52 md:py-1">
                  <Link href='/programas/medio' className="flex items-center justify-center font-bold text-sm rounded-full py-3 px-4 shadow-sm border-2 border-yellow md:py-3 md:px-8 hover:bg-yellow-dark/35 md:text-nowrap">
                    <p className="mr-2 text-white text-md md:text-lg">Descubrir más</p>
                    <FontAwesomeIcon icon={faChevronsRight} className="text-[9px] text-white" />
                  </Link>
                </div>
              </div>
            )}
            {displaySection === 2 && (
              <div className="z-10 px-4">
                <div className="mt-6">
                  <h2 className="font-thin text-[#FFDAC8] text-3xl md:text-5xl">Técnico <span className="font-bold">Profesional</span></h2>
                </div>
                <div className="py-2 md:py-6">
                  <p className="uppercase text-white max-w-md text-md px-2 md:text-xl">En <span className="font-bold">artes culinarias y emprendimiento con énfasis en gastronomía</span></p>
                  <p className="font-thin text-white mt-2 max-w-md text-sm px-2 md:text-lg md:max-w-lg"><span className="font-bold">Transfórmate en un chef altamente cualificado, que aporta valor diferencial a las demandas del mercado actual.</span> El programa profesional en gastronomía más innovador, diseñado cuidadosamente para desarrollar las destrezas para trabajar en establecimientos gastronómicos y restaurantes, junto a chefs profesionales de gran trayectoria.</p>
                </div>
                <div className="py-4 w-44 md:w-52 md:py-1">
                  <Link href='/programas/profesional' className="flex items-center justify-center font-bold text-sm rounded-full py-3 px-4 shadow-sm border-2 border-pink md:py-3 md:px-8 hover:bg-pink-dark/35 md:text-nowrap">
                    <p className="mr-2 text-white text-md md:text-lg">Descubrir más</p>
                    <FontAwesomeIcon icon={faChevronsRight} className="text-[9px] text-white" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}